import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { useAuthContext } from '@/contexts/AuthContext';
import EditPencilIcon from '@/assets/edit-pencil.svg?react';
import FeedbackModal from './FeedbackModal';
import {
  useBottomClearance,
  useReportBottomOverlay,
} from '@/lib/bottomOverlays';

// Gutter between the button and the bottom-right corner, matched to
// theme.spacing(3).
const MARGIN = 24;

const FeedbackFloatingButton = () => {
  const { user, isPending } = useAuthContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [buttonElement, setButtonElement] =
    useState<HTMLButtonElement | null>(null);

  // The upload manager keeps the corner when it is open, so the button rides
  // above it rather than sitting on top of it.
  const docked = useBottomClearance('right', ['feedbackButton']);

  useReportBottomOverlay('feedbackButton', 'right', buttonElement);

  if (!user || isPending) {
    return null;
  }

  return (
    <>
      <FloatingButton
        ref={setButtonElement}
        $bottom={Math.max(MARGIN, docked)}
        type="button"
        onClick={() => setModalOpen(true)}
        aria-label="Share feedback"
      >
        <EditPencilIcon aria-hidden />
        <FormattedMessage
          id="feedbackWidget.floatingButton"
          defaultMessage="Share feedback"
        />
      </FloatingButton>
      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

// Rides on top of whatever is docked in the bottom-right corner; the basket
// bar meanwhile shortens itself to clear the width reported to the store.
const FloatingButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$bottom',
})<{ $bottom: number }>(({ theme, $bottom }) => ({
  position: 'fixed',
  bottom: $bottom,
  right: MARGIN,
  zIndex: 1100,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: 48,
  padding: theme.spacing(0, 2.5),
  border: 'none',
  borderRadius: 24,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.white,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '20px',
  cursor: 'pointer',
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.2s ease-in-out',

  '> svg': {
    width: 20,
    height: 20,
    flexShrink: 0,

    path: {
      stroke: theme.palette.white,
    },
  },

  '&:hover': {
    backgroundColor: theme.palette.red600,
    boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.24)',
  },

  '&:active': {
    transform: 'scale(0.98)',
  },
}));

export default FeedbackFloatingButton;
